import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, UserCheck, User as UserIcon, KeyRound } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Input,
  Select,
  Modal,
  LoadingSpinner,
} from '../components/ui';
import { getUsers, addUser, updateUser, deleteUser, setUserPassword } from '../services/api';
import type { User, UserFormData } from '../types';
import { useAuth } from '../contexts/AuthContext';

const defaultFormData: UserFormData = {
  name: '',
  email: '',
  role: 'user',
  active: true,
};

export default function Admin() {
  const { user } = useAuth();
  const isRootAdmin = Boolean(user?.isRootAdmin);

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const [formData, setFormData] = useState<UserFormData>({
    ...defaultFormData,
    role: isRootAdmin ? 'admin' : 'user',
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [saving, setSaving] = useState(false);
  const [initialPassword, setInitialPassword] = useState('');
  const [actionError, setActionError] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const usersRes = await getUsers();
      
      if (usersRes.success) setUsers(usersRes.users);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      setActionError('');
      setSaving(true);
      const payload: UserFormData = {
        ...formData,
        ...(isRootAdmin ? { initialPassword } : {}),
      };

      const response = await addUser(payload);
      if (response.success && response.user) {
        setUsers([...users, response.user]);
        setShowAddModal(false);
        setFormData({ ...defaultFormData, role: isRootAdmin ? 'admin' : 'user' });
        setInitialPassword('');
      }
    } catch (error) {
      console.error('Failed to add user:', error);
      setActionError(error instanceof Error ? error.message : 'Failed to add user');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedUser) return;
    
    try {
      setSaving(true);
      const response = await updateUser(selectedUser.id, formData);
      if (response.success && response.user) {
        setUsers(users.map((u) => (u.id === selectedUser.id ? response.user! : u)));
        setShowEditModal(false);
        setSelectedUser(null);
        setFormData({ ...defaultFormData, role: isRootAdmin ? 'admin' : 'user' });
      }
    } catch (error) {
      console.error('Failed to update user:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    
    try {
      setSaving(true);
      const response = await deleteUser(selectedUser.id);
      if (response.success) {
        setUsers(users.filter((u) => u.id !== selectedUser.id));
        setShowDeleteModal(false);
        setSelectedUser(null);
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setActionError('');
    setFormData({
      name: user.name,
      email: user.email,
      role: isRootAdmin ? 'admin' : 'user',
      active: user.active,
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setActionError('');
    setShowDeleteModal(true);
  };

  const toggleUserStatus = async (user: User) => {
    try {
      const response = await updateUser(user.id, { active: !user.active });
      if (response.success && response.user) {
        setUsers(users.map((u) => (u.id === user.id ? response.user! : u)));
      }
    } catch (error) {
      console.error('Failed to toggle user status:', error);
    }
  };

  const openPasswordModal = (user: User) => {
    setPasswordTarget(user);
    setNewPassword('');
    setActionError('');
    setShowPasswordModal(true);
  };

  const handleSetPassword = async () => {
    if (!passwordTarget) return;
    try {
      setActionError('');
      setSaving(true);
      const response = await setUserPassword(passwordTarget.id, newPassword);
      if (response.success) {
        setShowPasswordModal(false);
        setPasswordTarget(null);
        setNewPassword('');
      }
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Failed to set password');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Admin</h1>
          <p className="text-dark-400 mt-1">
            {isRootAdmin ? 'Manage family accounts' : 'Manage family members'}
          </p>
        </div>
        
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="w-4 h-4" />
          {isRootAdmin ? 'Add Family Account' : 'Add Family Member'}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center">
              <UserIcon className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-dark-400 text-sm">{isRootAdmin ? 'Total Families' : 'Total Members'}</p>
              <p className="text-2xl font-bold text-white">{users.length}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-dark-400 text-sm">{isRootAdmin ? 'Active Families' : 'Active Members'}</p>
              <p className="text-2xl font-bold text-white">
                {users.filter((u) => u.active).length}
              </p>
            </div>
          </CardContent>
        </Card>

      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-white">{isRootAdmin ? 'Family Accounts' : 'Family Members'}</h2>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    {isRootAdmin ? 'Family Account' : 'Family Member'}
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-700">
                {users.length > 0 ? (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-dark-700/50 transition-colors">
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-primary-400 font-medium">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <div className="text-white font-medium truncate">{user.name}</div>
                            <div className="text-dark-400 text-sm truncate">{user.email || 'No email'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.role === 'admin'
                            ? 'bg-purple-500/20 text-purple-400'
                            : 'bg-dark-600 text-dark-300'
                        }`}>
                          {isRootAdmin ? 'family admin' : (user.role === 'admin' ? 'family admin' : 'member')}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-dark-300">
                        {isRootAdmin ? 'Family account' : 'Family member'}
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <button
                          onClick={() => toggleUserStatus(user)}
                          className={`px-2 py-1 text-xs rounded-full transition-colors ${
                            user.active
                              ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                              : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                          }`}
                        >
                          {user.active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openPasswordModal(user)}
                            className="p-2 text-dark-400 hover:text-amber-400 hover:bg-dark-700 rounded-lg transition-colors"
                            title="Set Password"
                          >
                            <KeyRound className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(user)}
                            className="p-2 text-dark-400 hover:text-primary-400 hover:bg-dark-700 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(user)}
                            className="p-2 text-dark-400 hover:text-red-400 hover:bg-dark-700 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 sm:px-6 py-8 text-center text-dark-400">
                      {isRootAdmin
                        ? 'No family accounts found. Add your first family account to get started.'
                        : 'No family members found. Add your first family member to get started.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setFormData({ ...defaultFormData, role: isRootAdmin ? 'admin' : 'user' });
          setInitialPassword('');
          setActionError('');
        }}
        title={isRootAdmin ? 'Add Family Account' : 'Add Family Member'}
      >
        <UserForm
          formData={formData}
          setFormData={setFormData}
          initialPassword={initialPassword}
          setInitialPassword={setInitialPassword}
          showInitialPassword={isRootAdmin}
          onSubmit={handleAdd}
          onCancel={() => {
            setShowAddModal(false);
            setFormData({ ...defaultFormData, role: isRootAdmin ? 'admin' : 'user' });
            setInitialPassword('');
            setActionError('');
          }}
          saving={saving}
          submitLabel={isRootAdmin ? 'Add Family Account' : 'Add Family Member'}
          roleLabel={isRootAdmin ? 'admin' : 'user'}
          actionError={actionError}
          isMemberMode={!isRootAdmin}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
          setFormData({ ...defaultFormData, role: isRootAdmin ? 'admin' : 'user' });
          setActionError('');
        }}
        title={isRootAdmin ? 'Edit Family Account' : 'Edit Family Member'}
      >
        <UserForm
          formData={formData}
          setFormData={setFormData}
          initialPassword=""
          setInitialPassword={() => {}}
          showInitialPassword={false}
          onSubmit={handleUpdate}
          onCancel={() => {
            setShowEditModal(false);
            setSelectedUser(null);
            setFormData({ ...defaultFormData, role: isRootAdmin ? 'admin' : 'user' });
            setActionError('');
          }}
          saving={saving}
          submitLabel="Save Changes"
          roleLabel={isRootAdmin ? 'admin' : 'user'}
          actionError={actionError}
          isMemberMode={!isRootAdmin}
        />
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
        }}
        title={isRootAdmin ? 'Delete Family Account' : 'Delete Family Member'}
      >
        <div className="space-y-4">
          <p className="text-dark-300">
            Are you sure you want to delete <span className="text-white font-medium">{selectedUser?.name}</span>?
            {isRootAdmin
              ? ' This will also delete all data for this family account.'
              : ' This will also delete subscriptions assigned to this family member.'}
          </p>
          <div className="flex gap-3 justify-end mt-6">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedUser(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={saving}>
              {saving ? <LoadingSpinner size="sm" /> : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Set Password Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setPasswordTarget(null);
          setNewPassword('');
          setActionError('');
        }}
        title="Set Password"
      >
        <div className="space-y-4">
          <p className="text-dark-300">
            Set a new password for <span className="text-white font-medium">{passwordTarget?.name}</span>.
            They will be required to change it on next login.
          </p>
          {actionError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              <p className="text-red-400 text-sm">{actionError}</p>
            </div>
          )}
          <Input
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Min 8 characters"
          />
          <div className="flex gap-3 justify-end mt-6">
            <Button
              variant="secondary"
              onClick={() => {
                setShowPasswordModal(false);
                setPasswordTarget(null);
                setNewPassword('');
                setActionError('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSetPassword} disabled={saving || newPassword.length < 8}>
              {saving ? <LoadingSpinner size="sm" /> : 'Set Password'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// User Form Component
interface UserFormProps {
  formData: UserFormData;
  setFormData: (data: UserFormData) => void;
  initialPassword: string;
  setInitialPassword: (value: string) => void;
  showInitialPassword: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  saving: boolean;
  submitLabel: string;
  roleLabel: 'admin' | 'user';
  actionError?: string;
  isMemberMode: boolean;
}

function UserForm({
  formData,
  setFormData,
  initialPassword,
  setInitialPassword,
  showInitialPassword,
  onSubmit,
  onCancel,
  saving,
  submitLabel,
  roleLabel,
  actionError,
  isMemberMode,
}: UserFormProps) {
  return (
    <div className="space-y-4">
      <Input
        label="Name"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        placeholder="Enter name"
        required
      />
      
      <Input
        label="Email"
        type="email"
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        placeholder={showInitialPassword ? 'Enter email (required)' : 'Enter email (optional)'}
        required={showInitialPassword}
      />

      {showInitialPassword ? (
        <Input
          label="Initial Password"
          type="password"
          value={initialPassword}
          onChange={(e) => setInitialPassword(e.target.value)}
          placeholder="At least 8 characters"
          required
        />
      ) : null}
      
      {!isMemberMode ? (
        <Select
          label="Role"
          value={formData.role}
          onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'user' })}
          options={roleLabel === 'admin'
            ? [{ value: 'admin', label: 'Admin' }]
            : [{ value: 'user', label: 'User' }]
          }
        />
      ) : (
        <p className="text-xs text-dark-400">
          Family members are profile-only records for subscription assignment and cannot log in.
        </p>
      )}
      
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="active"
          checked={formData.active}
          onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
          className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-primary-500"
        />
        <label htmlFor="active" className="text-dark-300">
          {isMemberMode ? 'Active member' : 'Active user'}
        </label>
      </div>

      {actionError ? <p className="text-sm text-red-400">{actionError}</p> : null}
      
      <div className="flex gap-3 justify-end mt-6">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={saving || !formData.name || (showInitialPassword && (!formData.email || initialPassword.length < 8))}
        >
          {saving ? <LoadingSpinner size="sm" /> : submitLabel}
        </Button>
      </div>
    </div>
  );
}
